/**
 * Helpers for safely extracting field values from onChange payloads, and for
 * repairing previously-persisted corrupted values at load time.
 *
 * Replaces the `e?.target?.value ?? e?.value ?? e` pattern in the form field
 * components, whose final `?? e` let a raw synthetic event object fall through
 * and get persisted to Mongo as the field value (data-safety audit finding C1).
 */

import { formFileRefsToDisplayString, isFormFileRefValue } from "../lib/validations/formFiles";

const isObjectLike = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v) && !(v instanceof Date);

/**
 * Extract the actual value from an input onChange payload.
 *
 * Contract:
 * - `undefined` / `null` → `""` (a cleared field is an empty string, never undefined)
 * - strings / numbers / booleans / `Date`s pass through untouched
 * - arrays pass through untouched (multi-select emissions)
 * - object with a `target` key (real or synthetic event) → `e.target?.value ?? ""` —
 *   an event object can never fall through and become the field value
 * - option-shaped object (`value` key, no `target`) → `e.value ?? ""`
 * - any other object → `""` (nothing a form field should store)
 */
// biome-ignore lint/suspicious/noExplicitAny: onChange payloads are inherently untyped here
export const extractInputValue = (e: any): any => {
  if (e === undefined || e === null) return "";
  if (typeof e === "string" || typeof e === "number" || typeof e === "boolean") return e;
  if (e instanceof Date) return e;
  if (Array.isArray(e)) return e;
  if (typeof e === "object") {
    if ("target" in e) return e.target?.value ?? "";
    if ("value" in e) return e.value ?? "";
    return "";
  }
  // function / symbol / bigint — nothing a form field should store.
  return "";
};

/**
 * Read-time repair for field values already persisted in a corrupted shape
 * (see audit C1). Applied at the single load chokepoint
 * (`populateTemplateWithSavedFields`) so corrupted rows display correctly and
 * self-heal on the next autosave.
 *
 * Only unwraps the two known corruption signatures — an event object (`target`
 * key) and a react-select option (`value` + `label` keys) — so legitimate
 * non-select object values are never mangled:
 * - persisted event object → unwrap `v.target?.value` (recursively), else `""`
 * - persisted option `{ value, label, ... }` → `.value ?? ""`
 * - arrays → normalize each element, drop `null` / `undefined`
 * - everything else untouched
 */
// biome-ignore lint/suspicious/noExplicitAny: persisted values are inherently untyped here
export const normalizeSavedFieldValue = (v: any): any => {
  if (Array.isArray(v)) {
    // A file-upload field value renders as a friendly filename list (PDF / read-only paths),
    // not "[object Object]". Empty arrays collapse to "" just like before.
    if (isFormFileRefValue(v)) return formFileRefsToDisplayString(v);
    return v.map((el) => normalizeSavedFieldValue(el)).filter((el) => el !== null && el !== undefined);
  }
  if (isObjectLike(v)) {
    if ("target" in v) {
      const inner = (v.target as Record<string, unknown> | undefined | null)?.value;
      return inner === undefined || inner === null ? "" : normalizeSavedFieldValue(inner);
    }
    if ("value" in v && "label" in v) return v.value ?? "";
  }
  return v;
};
